import {
  BottomSheetComponent,
  Modal,
  NavigationConfig,
  registerBottomSheets,
  registerNavigationHandlers,
  Route,
} from '@betty/navigation';

import {
  SOFAttestationScreen,
  AccountMFAScreen,
  ResponsibleGamingScreen,
  GamingHistoryScreen,
  CoinsGamingTransactionsScreen,
  NotificationsScreen,
  ContactUsScreen,
  TransactionHistoryScreen,
  MyProfileScreen,
  DepositLimitScreen,
  LossLimitScreen,
  PlaytimeLimitScreen,
  TimeoutScreen,
  SelfExclusionScreen,
  SelfExclusionVerifyScreen,
  SessionHistoryScreen,
} from '../screens';
import { FunctionComponent } from 'react';
import TimeoutBottomSheet from './ResponsibleGaming/TimeoutBottomSheet';
import SelfExclusionBottomSheet from './ResponsibleGaming/SelfExclusionBottomSheet';
import {
  CancelWithdrawal,
  ScreenNameInfoBottomSheet,
  SOFBottomSheet,
  CloseAccount,
} from '../bottomSheets';
import { ChangePasswordScreen } from '@betty/auth/screens';

export const NAVIGATION_CONFIG: NavigationConfig<AppNavigatorParamsList> = {
  FULL_SCREENS: [
    {
      name: 'SOFAttestation',
      options: { headerShown: true },
      component: SOFAttestationScreen,
    },
    {
      name: 'TransactionHistory',
      options: {},
      component: TransactionHistoryScreen,
    },
    {
      name: 'MyProfile',
      options: {},
      component: MyProfileScreen,
    },
    {
      name: 'TwoFactorAuthentication',
      options: { headerShown: true },
      component: AccountMFAScreen,
    },
    {
      name: 'ChangePasswordScreen',
      options: { headerShown: true },
      component: ChangePasswordScreen,
    },
    {
      name: 'GamingHistory',
      options: {},
      component: GamingHistoryScreen,
    },
    {
      name: 'CoinsGamingTransactions',
      options: {},
      component: CoinsGamingTransactionsScreen,
    },
    {
      name: 'SessionHistory',
      options: {},
      component: SessionHistoryScreen,
    },
    {
      name: 'ContactUsScreen',
      options: {},
      component: ContactUsScreen,
    },
    {
      name: 'Notifications',
      options: { headerShown: true },
      component: NotificationsScreen,
    },
    {
      name: 'ResponsibleGaming',
      options: { headerShown: true },
      component: ResponsibleGamingScreen,
    },
    {
      name: 'DepositLimit',
      options: { headerShown: true },
      component: DepositLimitScreen,
    },
    {
      name: 'LossLimit',
      options: { headerShown: true },
      component: LossLimitScreen,
    },
    {
      name: 'PlaytimeLimit',
      options: { headerShown: true },
      component: PlaytimeLimitScreen,
    },
    {
      name: 'Timeout',
      options: { headerShown: true },
      component: TimeoutScreen,
    },
    {
      name: 'SelfExclusion',
      options: { headerShown: true },
      component: SelfExclusionScreen,
    },
    {
      name: 'SelfExclusionVerify',
      options: {},
      component: SelfExclusionVerifyScreen as FunctionComponent<{
        route: Route<string, object | undefined>;
      }>,
    },
  ],
  FULL_SCREEN_MODALS: [],
  FULL_SCREEN_MODALS_TRANSPARENT: [],
};

const bottomSheets = {
  SOF: SOFBottomSheet,
  ScreenNameInfo: ScreenNameInfoBottomSheet,
  CancelWithdrawal,
  CloseAccount,
  Timeout: TimeoutBottomSheet,
  SelfExclusion: SelfExclusionBottomSheet,
};

registerBottomSheets('account', bottomSheets);

declare global {
  interface RegisteredBottomSheets {
    account: Record<keyof typeof bottomSheets, BottomSheetComponent>;
  }
}

registerNavigationHandlers<
  AppNavigatorParamsList,
  | 'MyProfile'
  | 'TransactionHistory'
  | 'GamingHistory'
  | 'CoinsGamingTransactions'
  | 'SessionHistory'
  | 'TwoFactorAuthentication'
  | 'Notifications'
  | 'ReferrerPromoScreen'
  | 'ResponsibleGaming'
  | 'DepositLimit'
  | 'LossLimit'
  | 'PlaytimeLimit'
  | 'Timeout'
  | 'SelfExclusion'
  | 'SelfExclusionVerify'
  | 'SOFAttestation'
  | 'ContactUsScreen'
>({
  MyProfile(navigate) {
    navigate('/account/details');
  },
  TransactionHistory(navigate) {
    navigate('/account/payment-history');
  },
  GamingHistory(navigate) {
    navigate('/account/game-transactions');
  },
  CoinsGamingTransactions(navigate) {
    navigate('/account/coins-game-transactions');
  },
  SessionHistory(navigate) {
    navigate('/account/sessions');
  },
  TwoFactorAuthentication() {
    Modal.open({
      namespace: 'account',
      type: 'TwoFactorAuthentication',
    });
  },
  Notifications() {
    Modal.open({
      namespace: 'account',
      type: 'Notifications',
    });
  },
  ReferrerPromoScreen(navigate, params) {
    const { path } = params ?? {};
    if (!path) {
      return;
    }
    navigate(path);
  },
  ResponsibleGaming(navigate) {
    navigate('/account/limits');
  },
  DepositLimit(navigate) {
    navigate('/account/limits/deposit');
  },
  LossLimit(navigate) {
    navigate('/account/limits/loss');
  },
  PlaytimeLimit(navigate) {
    navigate('/account/limits/playtime');
  },
  Timeout(navigate) {
    navigate('/account/limits/timeout');
  },
  SelfExclusion(navigate) {
    navigate('/account/limits/self-exclusion');
  },
  SelfExclusionVerify(navigate, params) {
    navigate('/account/limits/self-exclusion/verify', {
      state: params,
    });
  },
  SOFAttestation(navigate) {
    navigate('/account/source-of-funds');
  },
  ContactUsScreen(navigate) {
    navigate('/contact');
  },
});
